import React, { useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { TextField, MenuItem } from "@mui/material";

import "./App.css";

import { PROJECTS } from "./projects";

import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";

const ALL_LABEL = "All";

function App() {
  const [selectedLabel, setSelectedLabel] = useState<string>(ALL_LABEL);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [expandedBodies, setExpandedBodies] = useState<Record<string, boolean>>(
    {}
  );

  const labels = useMemo(() => {
    const set = new Set<string>();
    PROJECTS.forEach((p) => p.tags.forEach((t) => set.add(t)));
    return [ALL_LABEL, ...Array.from(set).sort()];
  }, []);

  // const filteredProjects =
  //   selectedLabel === ALL_LABEL
  //     ? PROJECTS
  //     : PROJECTS.filter((p) => p.tags.includes(selectedLabel));

  const filteredProjects = useMemo(() => {
    const base =
      selectedLabel === ALL_LABEL
        ? PROJECTS
        : PROJECTS.filter((p) => p.tags.includes(selectedLabel));

    return [...base].sort((a, b) =>
      a.title.replace("“", "").localeCompare(b.title.replace("“", ""))
    );
  }, [selectedLabel]);

  return (
    <Box className="App">
      <Box className="page">
        {/* Top block */}
        <Box className="page-header">
          {/* Name + email on one line */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "baseline",
              gap: 1,
            }}
          >
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontSize: "32px", lineHeight: "36px" }}
            >
              B.F. Lyon, Ph.D.
            </Typography>

            <Typography
              component="a"
              href="mailto:bradflyon@gmail.com"
              sx={{
                fontSize: "18px",
                color: "primary.main",
                textDecoration: "none",
                ml: "8px",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              bradflyon@gmail.com
            </Typography>
          </Box>

          {/* Subtitle */}
          <Typography
            variant="subtitle1"
            sx={{
              mt: "6px",
              fontStyle: "italic",
              fontWeight: 400,
              fontSize: "20px",
              lineHeight: "24px",
              color: "text.primary",
              marginTop: "10px",
              marginBottom: "18px",
            }}
          >
            I enjoy surfacing insight through interactive tools that invite
            exploration and discovery.
          </Typography>

          {/* Label bar: max ~2 rows, internal scroll if overflow */}
          <Box className="filterBar">
            {/* Label bar / filter */}
            {isMobile ? (
              // MOBILE: compact dropdown
              <TextField
                label="Filter"
                size="small"
                select
                value={selectedLabel}
                onChange={(e) => setSelectedLabel(e.target.value)}
                sx={{ mt: 1, minWidth: 180 }}
              >
                {labels.map((label) => (
                  <MenuItem key={label} value={label}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              // DESKTOP: full chip bar
              <Box className="filterBar">
                {labels.map((label) => (
                  <Button
                    key={label}
                    size="small"
                    variant={label === selectedLabel ? "contained" : "outlined"}
                    onClick={() => setSelectedLabel(label)}
                    sx={{
                      borderRadius: 999,
                      textTransform: "none",
                      mr: 0.5,
                      mb: 0.5,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {label}
                  </Button>
                ))}
              </Box>
            )}
            {/* {labels.map((label) => (
              <Button
                key={label}
                size="small"
                variant={label === selectedLabel ? "contained" : "outlined"}
                onClick={() => setSelectedLabel(label)}
                sx={{
                  borderRadius: 999,
                  textTransform: "none",
                  mr: 0.5,
                  mb: 0.5,
                  whiteSpace: "nowrap",
                }}
              >
                {label}
              </Button>
            ))} */}
          </Box>
        </Box>

        {/* Middle: content area */}
        <Box className="table-wrapper">
          {isMobile ? (
            // MOBILE: stacked cards
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, pb: 2 }}
            >
              {filteredProjects.map((p) => {
                const isExpanded = !!expandedBodies[p.id];

                return (
                  <Paper
                    key={p.id}
                    variant="outlined"
                    sx={{ p: 2, borderRadius: 2 }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, fontStyle: "italic", mb: 0.5 }}
                    >
                      {p.title}
                    </Typography>

                    {/* tags inline under title */}
                    {p.tags?.length > 0 && (
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 1,
                          fontStyle: "italic",
                          color: "text.secondary",
                        }}
                      >
                        (
                        {p.tags.map((tag, idx) => (
                          <React.Fragment key={tag}>
                            <span
                              onClick={() => setSelectedLabel(tag)}
                              style={{
                                cursor: "pointer",
                                textDecoration:
                                  tag === selectedLabel ? "underline" : "none",
                              }}
                            >
                              {"#" + tag.toLowerCase()}
                            </span>
                            {idx < p.tags.length - 1 && ", "}
                          </React.Fragment>
                        ))}
                        )
                      </Typography>
                    )}

                    {/* links */}
                    <Box sx={{ mb: 1 }}>
                      {p.primaryLink && (
                        <Box mb={0.5}>
                          <a
                            href={p.primaryLink}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Link
                          </a>
                        </Box>
                      )}
                      {p.extraLinks?.map((link) => (
                        <Box key={link.href} mb={0.5}>
                          <a href={link.href} target="_blank" rel="noreferrer">
                            {link.label}
                          </a>
                        </Box>
                      ))}
                    </Box>

                    {/* summary + body */}
                    {p.summary && (
                      <Typography
                        variant="body2"
                        className="project-summary"
                        sx={{ mb: 1 }}
                      >
                        {p.summary}
                      </Typography>
                    )}

                    {p.screenshots && p.screenshots.length > 0 && (
                      <Box className="screenshots-row">
                        {p.screenshots.map((src) => {
                          const resolvedSrc = (() => {
                            if (src.startsWith("http")) return src;
                            const base = process.env.PUBLIC_URL || "";
                            if (src.startsWith("/")) return `${base}${src}`;
                            return `${base}/${src}`;
                          })();

                          return (
                            <Box key={src} className="screenshot-wrapper">
                              <img
                                src={resolvedSrc}
                                className="screenshot-img"
                                loading="lazy"
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    )}

                    <div
                      className={
                        "desc-body " +
                        (isMobile && !isExpanded ? "desc-body--collapsed" : "")
                      }
                    >
                      {p.body}
                    </div>

                    {isMobile && (
                      <Button
                        size="small"
                        variant="text"
                        sx={{ mt: 1, textTransform: "none", pl: 0 }}
                        onClick={() =>
                          setExpandedBodies((prev) => ({
                            ...prev,
                            [p.id]: !prev[p.id],
                          }))
                        }
                      >
                        {isExpanded ? "Show less" : "Read more"}
                      </Button>
                    )}
                  </Paper>
                );
              })}
            </Box>
          ) : (
            // DESKTOP: keep your table as-is
            <TableContainer className="table-container">
              <Table
                stickyHeader
                size="small"
                aria-label="visualizations table"
                sx={{ minWidth: 650 }}
              >
                <TableHead sx={{ "& th": { fontWeight: 700 } }}>
                  <TableRow>
                    <TableCell sx={{ width: 220 }}>Title</TableCell>
                    <TableCell sx={{ width: 140 }}>Link</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredProjects.map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell
                        sx={{
                          verticalAlign: "top",
                          fontWeight: 600,
                          fontStyle: "italic",
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontStyle: "italic",
                          }}
                        >
                          {p.title}
                        </Typography>

                        {p.tags && p.tags.length > 0 && (
                          <Typography
                            variant="body2"
                            sx={{
                              mt: 0.5,
                              fontStyle: "italic",
                              color: "text.secondary",
                            }}
                          >
                            (
                            {p.tags.map((tag, idx) => (
                              <React.Fragment key={tag}>
                                <span
                                  onClick={() => setSelectedLabel(tag)}
                                  style={{
                                    cursor: "pointer",
                                    textDecoration:
                                      tag === selectedLabel
                                        ? "underline"
                                        : "none",
                                  }}
                                >
                                  {"#" + tag.toLocaleLowerCase()}
                                </span>
                                {idx < p.tags.length - 1 && ", "}
                              </React.Fragment>
                            ))}
                            )
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        {p.primaryLink && (
                          <Box mb={0.5}>
                            <a
                              href={p.primaryLink}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Link
                            </a>
                          </Box>
                        )}
                        {p.extraLinks?.map((link) => (
                          <Box key={link.href} mb={0.5}>
                            <a
                              href={link.href}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {link.label}
                            </a>
                          </Box>
                        ))}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: "top" }}>
                        {p.summary && (
                          <p className="project-summary">{p.summary}</p>
                        )}

                        {/* {p.screenshots && p.screenshots.length > 0 && (
                          <Box className="screenshots-row">
                            {p.screenshots.map((src, idx) => (
                              <Box key={src} className="screenshot-wrapper">
                                <img
                                  src={src}
                                  // alt={`${p.title} screenshot ${idx + 1}`}
                                  className="screenshot-img"
                                  loading="lazy"
                                />
                              </Box>
                            ))}
                          </Box>
                        )} */}

                        {p.screenshots && p.screenshots.length > 0 && (
                          <Box className="screenshots-row">
                            {p.screenshots.map((src) => {
                              const resolvedSrc = (() => {
                                if (src.startsWith("http")) return src; // external images

                                const base = process.env.PUBLIC_URL || "";
                                // if src already starts with "/", avoid doubling slashes
                                if (src.startsWith("/")) {
                                  return `${base}${src}`;
                                }
                                return `${base}/${src}`;
                              })();

                              return (
                                <Box key={src} className="screenshot-wrapper">
                                  <img
                                    src={resolvedSrc}
                                    className="screenshot-img"
                                    loading="lazy"
                                  />
                                </Box>
                              );
                            })}
                          </Box>
                        )}
                        {/* Wrap body so mobile fade/ellipsis CSS can apply */}
                        <div className="desc-body">{p.body}</div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default App;
